import { useState } from 'react';
import {
  FileText, Filter, RefreshCw, Download 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { BidCard } from '@/features/sourcing';
import {
  useSupplierBids, useWithdrawBid 
} from '@/hooks/use-sourcing';
import {
  type BidStatus, BID_STATUS_LABELS 
} from '@/types/sourcing';
import { toast } from 'sonner';

const STATUS_FILTERS: (BidStatus | 'all')[] = ['all', 'submitted', 'under_review', 'accepted', 'rejected'];

export function MyBidsPage() {
  const [statusFilter, setStatusFilter] = useState<BidStatus | 'all'>('all');
  const [withdrawTarget, setWithdrawTarget] = useState<string | null>(null);
  const {
    data, isLoading, error, refetch 
  } = useSupplierBids();
  const withdrawBid = useWithdrawBid();

  const handleWithdraw = (bidId: string) => {
    setWithdrawTarget(bidId);
  };

  const handleConfirmWithdraw = () => {
    if (withdrawTarget) {
      withdrawBid.mutate(withdrawTarget);
      setWithdrawTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-48 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Failed to load bids</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allBids = data?.data ?? [];
  const filteredBids = statusFilter === 'all' 
    ? allBids 
    : allBids.filter((b) => b.status === statusFilter);

  const handleExport = () => {
    if (allBids.length === 0) {
      toast.info('No data to export');
      return;
    }
    const exportData = allBids.map((b) => ({
      bidId: b.bidId,
      eventId: b.eventId,
      status: b.status,
      total: b.total,
      currency: b.currency,
      submittedAt: b.submittedAt,
    }));
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map((row) =>
        headers.map((h) => {
          const val = row[h as keyof typeof row];
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replaceAll('"', '""')}"`;
          }
          return String(val ?? '');
        }).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'my-bids.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${allBids.length} bids`);
  };

  return (
    <div className="space-y-6">
      <PageHeader />

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {STATUS_FILTERS.map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'All Bids' : BID_STATUS_LABELS[status]}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {filteredBids.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No bids found</p>
            <p className="text-muted-foreground">
              {statusFilter === 'all'
                ? 'You have not submitted any bids yet'
                : `No bids with status "${BID_STATUS_LABELS[statusFilter]}"`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredBids.map((bid) => (
            <BidCard
              key={bid.bidId}
              bid={bid}
              onWithdraw={handleWithdraw}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={withdrawTarget !== null}
        title="Withdraw Bid"
        message="Are you sure you want to withdraw this bid? This action cannot be undone."
        confirmLabel="Withdraw"
        variant="warning"
        onConfirm={handleConfirmWithdraw}
        onCancel={() => setWithdrawTarget(null)}
      />
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold">My Bids</h1>
      <p className="text-muted-foreground">
        Track and manage your submitted bids
      </p>
    </div>
  );
}
